/** Phase 5a client auth — session cookie against the Stock Forge+ proxy. */

export interface AuthUser {
  id: string;
  email: string;
  emailVerified?: boolean;
}

export interface AuthMeResponse {
  ok: boolean;
  authenticated: boolean;
  method: "session" | null;
  user: AuthUser | null;
  bootstrapConfigured?: boolean;
  /** When false, self-serve /auth/register and Create account UI are closed. */
  registrationOpen?: boolean;
}

export interface SfCredentialsStatus {
  ok: boolean;
  configured: boolean;
  source: "user" | "env" | null;
  hint: string | null;
  updatedAt: string | null;
}

function authHeaders(): Record<string, string> {
  return { Accept: "application/json", "Content-Type": "application/json" };
}

export async function fetchAuthMe(): Promise<AuthMeResponse> {
  const res = await fetch("/auth/me", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    throw new Error(`auth/me failed (${res.status})`);
  }
  return (await res.json()) as AuthMeResponse;
}

export async function startDesktopPair(): Promise<{
  ok: true;
  code: string;
  pollSecret: string;
  expiresInMs: number;
}> {
  const res = await fetch("/auth/desktop-pair/start", {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: "{}",
  });
  const payload = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    code?: string;
    pollSecret?: string;
    expiresInMs?: number;
    detail?: { message?: string };
  };
  if (!res.ok || !payload.code || !payload.pollSecret) {
    throw new Error(payload.detail?.message ?? `Could not start sign-in (${res.status})`);
  }
  return {
    ok: true,
    code: payload.code,
    pollSecret: payload.pollSecret,
    expiresInMs: payload.expiresInMs ?? 180_000,
  };
}

export async function approveDesktopPair(code: string): Promise<void> {
  const res = await fetch("/auth/desktop-pair/approve", {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify({ code }),
  });
  const payload = (await res.json().catch(() => ({}))) as { detail?: { message?: string } };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `Could not finish sign-in (${res.status})`);
  }
}

export async function pollDesktopPair(
  code: string,
  pollSecret: string,
): Promise<{ pending: true } | { pending: false; user: AuthUser }> {
  const res = await fetch("/auth/desktop-pair/poll", {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify({ code, pollSecret }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    pending?: boolean;
    user?: AuthUser;
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `Sign-in failed (${res.status})`);
  }
  if (payload.pending) return { pending: true };
  if (!payload.user) throw new Error("Sign-in failed");
  return { pending: false, user: payload.user };
}

export async function loginWithPassword(
  email: string,
  password: string,
): Promise<{ ok: true; user: AuthUser }> {
  const res = await fetch("/auth/login", {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify({ email, password }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    user?: AuthUser;
    detail?: { message?: string };
  };
  if (!res.ok || !payload.user) {
    throw new Error(payload.detail?.message ?? `Login failed (${res.status})`);
  }
  return { ok: true, user: payload.user };
}

export async function registerWithPassword(
  email: string,
  password: string,
): Promise<{ ok: true; user: AuthUser; verificationEmailSent?: boolean }> {
  const res = await fetch("/auth/register", {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify({ email, password }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    user?: AuthUser;
    verificationEmailSent?: boolean;
    detail?: { message?: string };
  };
  if (!res.ok || !payload.user) {
    throw new Error(payload.detail?.message ?? `Create account failed (${res.status})`);
  }
  return {
    ok: true,
    user: payload.user,
    verificationEmailSent: payload.verificationEmailSent,
  };
}

export async function requestPasswordReset(
  email: string,
): Promise<{ ok: true; message: string; resetUrl?: string }> {
  const res = await fetch("/auth/forgot-password", {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify({ email }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    message?: string;
    resetUrl?: string;
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `Forgot password failed (${res.status})`);
  }
  return {
    ok: true,
    message: payload.message ?? "If an account exists for that email, we sent password reset instructions.",
    ...(payload.resetUrl ? { resetUrl: payload.resetUrl } : {}),
  };
}

export async function resetPasswordWithToken(
  token: string,
  newPassword: string,
): Promise<{ ok: true; message: string }> {
  const res = await fetch("/auth/reset-password", {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify({ token, newPassword }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    message?: string;
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `Reset password failed (${res.status})`);
  }
  return { ok: true, message: payload.message ?? "Password updated." };
}

export async function verifyEmailWithToken(
  token: string,
): Promise<{ ok: true; user?: AuthUser; message: string }> {
  const res = await fetch("/auth/verify-email", {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify({ token }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    user?: AuthUser;
    message?: string;
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `Verify email failed (${res.status})`);
  }
  return {
    ok: true,
    message: payload.message ?? "Email verified.",
    ...(payload.user ? { user: payload.user } : {}),
  };
}

export async function resendVerificationEmail(): Promise<{
  ok: true;
  message: string;
  alreadyVerified?: boolean;
  verificationUrl?: string;
}> {
  const res = await fetch("/auth/resend-verification", {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: "{}",
  });
  const payload = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    message?: string;
    alreadyVerified?: boolean;
    verificationUrl?: string;
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `Resend failed (${res.status})`);
  }
  return {
    ok: true,
    message: payload.message ?? "Verification email sent.",
    ...(payload.alreadyVerified ? { alreadyVerified: true } : {}),
    ...(payload.verificationUrl ? { verificationUrl: payload.verificationUrl } : {}),
  };
}

export async function logoutSession(): Promise<void> {
  await fetch("/auth/logout", {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: "{}",
  });
}

export async function fetchSfCredentialsStatus(): Promise<SfCredentialsStatus> {
  const res = await fetch("/sf-credentials", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const payload = (await res.json().catch(() => ({}))) as SfCredentialsStatus & {
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `sf-credentials failed (${res.status})`);
  }
  return payload;
}

export async function saveSfApiKey(apiKey: string): Promise<SfCredentialsStatus> {
  const res = await fetch("/sf-credentials", {
    method: "PUT",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify({ apiKey }),
  });
  const payload = (await res.json().catch(() => ({}))) as SfCredentialsStatus & {
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `Save API key failed (${res.status})`);
  }
  return payload;
}

export async function clearSfApiKey(): Promise<SfCredentialsStatus> {
  const res = await fetch("/sf-credentials", {
    method: "DELETE",
    credentials: "include",
    headers: authHeaders(),
  });
  const payload = (await res.json().catch(() => ({}))) as SfCredentialsStatus & {
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `Clear API key failed (${res.status})`);
  }
  return payload;
}

export async function logoutAllSessions(): Promise<{ ok: true; revoked: number }> {
  const res = await fetch("/auth/logout-all", {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: "{}",
  });
  const payload = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    revoked?: number;
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `Logout-all failed (${res.status})`);
  }
  return { ok: true, revoked: Number(payload.revoked) || 0 };
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: true; revokedOtherSessions: number }> {
  const res = await fetch("/auth/change-password", {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    revokedOtherSessions?: number;
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `Change password failed (${res.status})`);
  }
  return {
    ok: true,
    revokedOtherSessions: Number(payload.revokedOtherSessions) || 0,
  };
}

export async function downloadTenantBackup(): Promise<unknown> {
  const res = await fetch("/tenant/backup", {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = (payload as { detail?: { message?: string } }).detail;
    throw new Error(detail?.message ?? `Backup failed (${res.status})`);
  }
  return payload;
}

export async function restoreTenantBackup(backup: unknown): Promise<{ restored: string[] }> {
  const res = await fetch("/tenant/restore", {
    method: "PUT",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify({ confirm: true, backup }),
  });
  const payload = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    restored?: string[];
    detail?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(payload.detail?.message ?? `Restore failed (${res.status})`);
  }
  return { restored: payload.restored ?? [] };
}
